import { useState, useEffect } from 'react';
import { getElectronicInvoices, getElectronicInvoiceStats, type ElectronicInvoice, type ElectronicInvoiceStats } from '../../api/electronicInvoices.api';
import { getMe } from '../../api/auth.api';

export default function FacturasElectronicas() {
    const [invoices, setInvoices] = useState<ElectronicInvoice[]>([]);
    const [stats, setStats] = useState<ElectronicInvoiceStats | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isBillingEnabled, setIsBillingEnabled] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    const [activeTab, setActiveTab] = useState<'TODAS' | 'EMITIDAS' | 'PENDIENTES' | 'ERRORES'>('TODAS');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setIsLoading(true); // Show loading state when refreshing
        try {
            // Primero verificamos el estado de facturación
            const user = await getMe();
            if (user.company) {
                setIsBillingEnabled(user.company.billingEnabled);
                
                // Si no está habilitada, no cargamos el resto de datos
                if (!user.company.billingEnabled) {
                    setIsLoading(false);
                    return;
                }
            }

            const [invoicesData, statsData] = await Promise.all([
                getElectronicInvoices(),
                getElectronicInvoiceStats()
            ]);
            setInvoices(invoicesData);
            setStats(statsData);
        } catch (error) {
            console.error('Error cargando facturas electrónicas:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const getStatusColor = (estado: string) => {
        switch (estado) {
            case 'EMITIDA': return 'bg-green-100 text-green-700';
            case 'VALIDADA': return 'bg-blue-100 text-blue-700';
            case 'ERROR': return 'bg-red-100 text-red-700';
            case 'PENDIENTE': return 'bg-yellow-100 text-yellow-700';
            default: return 'bg-slate-100 text-slate-600';
        }
    };

    const getTipoClienteLabel = (tipo: string) => {
        return tipo === 'CLIENTE_IDENTIFICADO' ? 'Identificado' : 'Consumidor Final';
    };

    const filteredInvoices = invoices.filter(inv => {
        const matchesSearch = inv.numero.toLowerCase().includes(searchTerm.toLowerCase()) ||
            inv.clienteNombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
            inv.clienteDocumento.includes(searchTerm);
        
        let matchesTab = true;
        if (activeTab === 'EMITIDAS') matchesTab = inv.estado === 'EMITIDA' || inv.estado === 'VALIDADA';
        if (activeTab === 'PENDIENTES') matchesTab = inv.estado === 'PENDIENTE';
        if (activeTab === 'ERRORES') matchesTab = inv.estado === 'ERROR';

        return matchesSearch && matchesTab;
    });

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0
        }).format(amount);
    };

    return (
            <div className="space-y-6">
                {/* Modal de Funcionalidad Inactiva */}
                {!isBillingEnabled && !isLoading && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
                        <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl border border-slate-200 text-center animate-in fade-in zoom-in duration-200">
                            <div className="w-20 h-20 bg-orange-50 rounded-full flex items-center justify-center mx-auto mb-6">
                                <i className="ri-lock-2-line text-4xl text-orange-500"></i>
                            </div>
                            <h2 className="text-2xl font-bold text-slate-800 mb-3">Módulo Inactivo</h2>
                            <p className="text-slate-600 mb-8 leading-relaxed">
                                La funcionalidad de <strong>Facturación Electrónica</strong> no está activa en tu plan actual.
                            </p>
                            
                            <div className="space-y-4">
                                <button 
                                    onClick={() => window.open('https://api.whatsapp.com/send?phone=573008515255', '_blank')}
                                    className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-xl transition-all shadow-lg shadow-teal-600/20"
                                >
                                    <i className="ri-whatsapp-line text-xl"></i>
                                    Activar con Soporte
                                </button>
                                
                                <button
                                    onClick={() => window.history.back()}
                                    className="w-full px-6 py-3 text-slate-500 hover:text-slate-700 font-medium rounded-xl transition hover:bg-slate-50"
                                >
                                    Volver al Dashboard
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-800">Facturas Electrónicas</h1>
                        <p className="text-slate-500 mt-1">Historial de facturas emitidas a clientes</p>
                    </div>
                    <button 
                        onClick={() => loadData()}
                        className="px-4 py-2 bg-white border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50 transition flex items-center gap-2"
                    >
                        <i className={`ri-refresh-line ${isLoading ? 'animate-spin' : ''}`}></i>
                        Actualizar
                    </button>
                </div>

                {/* Stats Cards */}
                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-white rounded-xl p-4 border border-slate-200">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center">
                                    <i className="ri-file-list-3-line text-teal-600 text-lg"></i>
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
                                    <p className="text-xs text-slate-500">Total Facturas</p>
                                </div>
                            </div>
                        </div>
                        <div className="bg-white rounded-xl p-4 border border-slate-200">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                                    <i className="ri-checkbox-circle-line text-green-600 text-lg"></i>
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-green-600">{stats.emitidas}</p>
                                    <p className="text-xs text-slate-500">Emitidas</p>
                                </div>
                            </div>
                        </div>
                        <div className="bg-white rounded-xl p-4 border border-slate-200">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                                    <i className="ri-error-warning-line text-red-600 text-lg"></i>
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-red-600">{stats.errores}</p>
                                    <p className="text-xs text-slate-500">Con Error</p>
                                </div>
                            </div>
                        </div>
                        <div className="bg-white rounded-xl p-4 border border-slate-200">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                                    <i className="ri-money-dollar-circle-line text-blue-600 text-lg"></i>
                                </div>
                                <div>
                                    <p className="text-lg font-bold text-slate-800">{formatCurrency(stats.montoTotal)}</p>
                                    <p className="text-xs text-slate-500">Total Facturado</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Search & Tabs */}
                <div className="flex flex-col gap-4">
                     {/* Tabs */}
                    <div className="flex gap-2 overflow-x-auto pb-2">
                        {(['TODAS', 'EMITIDAS', 'PENDIENTES', 'ERRORES'] as const).map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`px-4 py-2 rounded-xl text-sm font-medium transition whitespace-nowrap ${
                                    activeTab === tab 
                                        ? 'bg-slate-800 text-white shadow-lg shadow-slate-200' 
                                        : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
                                }`}
                            >
                                {tab === 'PENDIENTES' && invoices.some(i => i.estado === 'PENDIENTE') && (
                                    <span className="w-2 h-2 bg-yellow-400 rounded-full inline-block mr-2"></span>
                                )}
                                {tab.charAt(0) + tab.slice(1).toLowerCase()}
                            </button>
                        ))}
                    </div>

                    <div className="bg-white p-4 rounded-xl border border-slate-200">
                        <div className="relative">
                            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"></i>
                            <input
                                type="text"
                                placeholder="Buscar por número, cliente o documento..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
                            />
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Número</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Cliente</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Tipo</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Fecha</th>
                                    <th className="px-6 py-4 text-right text-xs font-semibold text-slate-600 uppercase">Monto</th>
                                    <th className="px-6 py-4 text-center text-xs font-semibold text-slate-600 uppercase">Estado</th>
                                    <th className="px-6 py-4 text-center text-xs font-semibold text-slate-600 uppercase">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200">
                                {isLoading ? (
                                    <tr>
                                        <td colSpan={7} className="px-6 py-12 text-center text-slate-500">
                                            <i className="ri-loader-4-line animate-spin text-3xl text-teal-600"></i>
                                            <p className="mt-2">Cargando facturas...</p>
                                        </td>
                                    </tr>
                                ) : filteredInvoices.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="px-6 py-12 text-center text-slate-500">
                                            <i className="ri-file-forbid-line text-4xl text-slate-300"></i>
                                            <p className="mt-2">No se encontraron facturas con estos filtros</p>
                                        </td>
                                    </tr>
                                ) : (
                                    filteredInvoices.map((invoice) => (
                                        <tr key={invoice.id} className="hover:bg-slate-50 transition">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="font-medium text-slate-800">{invoice.numero}</div>
                                                {invoice.cufe && (
                                                    <div className="text-xs text-slate-400 truncate max-w-[150px]" title={invoice.cufe}>
                                                        CUFE: {invoice.cufe.substring(0, 12)}...
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="font-medium text-slate-800">{invoice.clienteNombre}</div>
                                                <div className="text-xs text-slate-500">{invoice.clienteDocumento}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 py-1 rounded-lg text-xs font-medium ${
                                                    invoice.tipoCliente === 'CLIENTE_IDENTIFICADO' 
                                                        ? 'bg-purple-100 text-purple-700' 
                                                        : 'bg-slate-100 text-slate-600'
                                                }`}>
                                                    {getTipoClienteLabel(invoice.tipoCliente)}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                                                {new Date(invoice.createdAt).toLocaleDateString('es-CO', {
                                                    day: '2-digit',
                                                    month: 'short',
                                                    year: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right font-bold text-slate-800">
                                                {formatCurrency(invoice.monto)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-center">
                                                <span className={`px-2.5 py-1 rounded-lg text-xs font-medium ${getStatusColor(invoice.estado)}`}>
                                                    {invoice.estado}
                                                </span>
                                                {invoice.estado === 'ERROR' && invoice.errorMensaje && (
                                                    <div className="text-xs text-red-500 mt-1 max-w-[150px] truncate" title={invoice.errorMensaje}>
                                                        {invoice.errorMensaje}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-center">
                                                <div className="flex items-center justify-center gap-2">
                                                    {invoice.estado === 'PENDIENTE' ? (
                                                        <button 
                                                            onClick={() => loadData()}
                                                            className="px-3 py-1 bg-yellow-100 hover:bg-yellow-200 text-yellow-700 rounded-lg text-xs font-medium transition flex items-center gap-1"
                                                            title="Verificar estado en DIAN"
                                                        >
                                                            <i className="ri-refresh-line"></i> Verificar
                                                        </button>
                                                    ) : (
                                                        <>
                                                            {invoice.pdfUrl ? (
                                                                <a 
                                                                    href={invoice.pdfUrl} 
                                                                    target="_blank" 
                                                                    rel="noopener noreferrer"
                                                                    className="w-8 h-8 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center text-red-600 transition"
                                                                    title="Ver PDF"
                                                                >
                                                                    <i className="ri-file-pdf-2-line"></i>
                                                                </a>
                                                            ) : (
                                                                <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center text-slate-300">
                                                                    <i className="ri-file-pdf-2-line"></i>
                                                                </div>
                                                            )}
                                                            {invoice.xmlUrl ? (
                                                                <a 
                                                                    href={invoice.xmlUrl} 
                                                                    target="_blank" 
                                                                    rel="noopener noreferrer"
                                                                    className="w-8 h-8 rounded-lg bg-blue-50 hover:bg-blue-100 flex items-center justify-center text-blue-600 transition"
                                                                    title="Ver XML"
                                                                >
                                                                    <i className="ri-file-code-line"></i>
                                                                </a>
                                                            ) : (
                                                                <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center text-slate-300">
                                                                    <i className="ri-file-code-line"></i>
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
    );
}
